import { supabase } from "@/integrations/supabase/client";

export type CommunicationContext = "customer" | "expert";

export const getMessagesPathForContext = (context: CommunicationContext): string =>
  context === "expert" ? "/expert/messages" : "/customer/messages";

export const getConversationPathForContext = (
  context: CommunicationContext,
  conversationId: string,
): string => `${getMessagesPathForContext(context)}?conversation=${encodeURIComponent(conversationId)}`;

export const getCommunicationContextFromPath = (pathname: string): CommunicationContext | null => {
  if (pathname === "/customer" || pathname.startsWith("/customer/")) {
    return "customer";
  }

  if (pathname === "/expert" || pathname.startsWith("/expert/")) {
    return "expert";
  }

  return null;
};

export interface ProjectCommunicationContexts {
  customerProjectIds: Set<string>;
  expertProjectIds: Set<string>;
}

export const getProjectCommunicationContexts = async (
  userId: string,
  projectIds: string[],
): Promise<ProjectCommunicationContexts> => {
  const uniqueProjectIds = [...new Set(projectIds.filter(Boolean))];
  const customerProjectIds = new Set<string>();
  const expertProjectIds = new Set<string>();

  if (uniqueProjectIds.length === 0) {
    return { customerProjectIds, expertProjectIds };
  }

  const [projectsResult, offersResult, assignmentsResult] = await Promise.all([
    supabase
      .from("projects")
      .select("id, user_id")
      .in("id", uniqueProjectIds),
    supabase
      .from("offers")
      .select("project_id")
      .eq("contractor_id", userId)
      .in("project_id", uniqueProjectIds),
    supabase
      .from("project_experts")
      .select("project_id")
      .eq("expert_id", userId)
      .in("project_id", uniqueProjectIds),
  ]);

  if (projectsResult.error) throw projectsResult.error;
  if (offersResult.error) throw offersResult.error;
  if (assignmentsResult.error) throw assignmentsResult.error;

  (projectsResult.data || []).forEach((project) => {
    if (project.user_id === userId) {
      customerProjectIds.add(project.id);
    }
  });

  [...(offersResult.data || []), ...(assignmentsResult.data || [])].forEach((relationship) => {
    if (relationship.project_id && !customerProjectIds.has(relationship.project_id)) {
      expertProjectIds.add(relationship.project_id);
    }
  });

  return { customerProjectIds, expertProjectIds };
};

export const getConversationIdsForContext = async (
  userId: string,
  context: CommunicationContext,
): Promise<string[]> => {
  const { data: conversations, error } = await supabase
    .from("conversations")
    .select("id, project_id")
    .or(`participant_1_id.eq.${userId},participant_2_id.eq.${userId}`);

  if (error) throw error;
  if (!conversations?.length) return [];

  const contexts = await getProjectCommunicationContexts(
    userId,
    conversations.map((conversation) => conversation.project_id),
  );
  const allowedProjectIds = context === "customer"
    ? contexts.customerProjectIds
    : contexts.expertProjectIds;

  return conversations
    .filter((conversation) => allowedProjectIds.has(conversation.project_id))
    .map((conversation) => conversation.id);
};
